import type { Cohort, AgeBracket, EducationLevel, IncomeLevel } from '@/types'
import { IdeologyDisplay } from '@/components/ideology/IdeologyDisplay'
import { SalienceDisplay } from '@/components/ideology/SalienceDisplay'
import { cn } from '@/lib/utils'

interface CohortDetailProps {
  cohort: Cohort
  className?: string
}

const ageBracketLabels: Record<AgeBracket, string> = {
  youth: 'Youth (18-24)',
  young_adult: 'Young Adult (25-39)',
  middle_aged: 'Middle Aged (40-59)',
  senior: 'Senior (60+)',
}

const educationLevelLabels: Record<EducationLevel, string> = {
  low: 'Low Education',
  medium: 'Medium Education',
  high: 'High Education',
}

const incomeLevelLabels: Record<IncomeLevel, string> = {
  low: 'Low Income',
  middle: 'Middle Income',
  high: 'High Income',
}

function formatPopulation(population: number): string {
  return population.toLocaleString()
}

function formatTurnout(turnout: number): string {
  return `${Math.round(turnout * 100)}%`
}

/**
 * Full detail view of a single cohort showing all information.
 */
export function CohortDetail({ cohort, className }: CohortDetailProps) {
  const effectiveTurnout = cohort.base_turnout * (1 + cohort.current_turnout_modifier)

  return (
    <div className={cn('space-y-6', className)}>
      {/* Demographics Section */}
      <section>
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Demographics
        </h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Age Bracket</span>
            <span className="font-medium">{ageBracketLabels[cohort.age_bracket]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Education</span>
            <span className="font-medium">{educationLevelLabels[cohort.education_level]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Income Level</span>
            <span className="font-medium">{incomeLevelLabels[cohort.income_level]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Religion</span>
            <span className="font-medium capitalize">{cohort.religion}</span>
          </div>
        </div>
      </section>

      {/* Population & Turnout Section */}
      <section>
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Population & Turnout
        </h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Population</span>
            <span className="font-medium">{formatPopulation(cohort.population)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Base Turnout</span>
            <span className="font-medium">{formatTurnout(cohort.base_turnout)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Turnout Modifier</span>
            <span className={cn(
              'font-medium',
              cohort.current_turnout_modifier > 0 && 'text-green-600',
              cohort.current_turnout_modifier < 0 && 'text-red-600'
            )}>
              {cohort.current_turnout_modifier > 0 ? '+' : ''}
              {Math.round(cohort.current_turnout_modifier * 100)}%
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Effective Turnout</span>
            <span className="font-medium">{formatTurnout(Math.min(1, effectiveTurnout))}</span>
          </div>
        </div>
      </section>

      {/* Ideology Section */}
      <section>
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Ideology Profile
        </h4>
        <IdeologyDisplay ideology={cohort.ideology_vector} showLabels />
      </section>

      {/* Salience Section */}
      <section>
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Issue Priorities
        </h4>
        <SalienceDisplay salience={cohort.salience_weights} />
      </section>
    </div>
  )
}

/**
 * Skeleton loader for CohortDetail
 */
export function CohortDetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <section>
        <div className="h-4 w-24 bg-muted rounded mb-3" />
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex justify-between">
              <div className="h-4 w-20 bg-muted rounded" />
              <div className="h-4 w-24 bg-muted rounded" />
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="h-4 w-32 bg-muted rounded mb-3" />
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-6 bg-muted rounded" />
          ))}
        </div>
      </section>
    </div>
  )
}
